import { CellWithTooltip } from "@/components/data-table/CellWithTooltip";
import { ColumnHeader } from "@/components/data-table/ColumnHeader";
import { Badge } from "@/components/ui/badge";
import { StandingOfferStatus } from "@/gql/__generated__/graphql";
import { cn } from "@/lib/utils";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { StandingOfferActions } from "../components/StandingOffers/StandingOfferActions";
import { StandingOfferTable } from "../types/subscriptions.type";
import { sortByPrice } from "../utils/sortByPrice";

type StandingOfferColumn = ColumnDef<StandingOfferTable>;

const imageColumn: StandingOfferColumn = {
  accessorKey: "appImage",
  cell: ({ row }) => (
    <div className="h-full w-fit">
      <img
        className="object-cover h-12 rounded"
        src={row.getValue("appImage") || "/app-placeholder-picture.png"}
        alt={row.getValue("appImage") || ""}
      />
    </div>
  ),
  header: undefined,
  enableHiding: false
};

const nameColumn: StandingOfferColumn = {
  accessorKey: "appName",
  cell: ({ row }) => <CellWithTooltip cellValue={row.getValue("appName")} />,
  header: ({ column }) => <ColumnHeader column={column} title="App" />,
  enableHiding: false
};

const statusColumn: StandingOfferColumn = {
  accessorKey: "status",
  cell: ({ row }) => {
    const status: StandingOfferStatus = row.getValue("status");
    return (
      <Badge variant="secondary" className="font-normal">
        <div
          className={cn(
            "w-2 h-2 rounded-full mr-2",
            {
              [StandingOfferStatus.Active]: "bg-brand-primary",
              [StandingOfferStatus.Cancelled]: "bg-brand-error"
            }[status]
          )}
        />
        <div
          className="lowercase first-letter:capitalize dark:text-brand-gray-200"
          data-testid="subscriptionOfferStatus"
        >
          {status}
        </div>
      </Badge>
    );
  },
  header: ({ column }) => <ColumnHeader column={column} title="Status" />,
  enableHiding: false
};

const createdColumn: StandingOfferColumn = {
  accessorKey: "createdAt",
  cell: ({ row }) => <span>{format(row.getValue("createdAt"), "PP")}</span>,
  header: ({ column }) => <ColumnHeader column={column} title="Created" />,
  enableHiding: false
};

const priceColumn: StandingOfferColumn = {
  accessorKey: "monthlyPriceUSD",
  cell: ({ row }) => {
    const price: StandingOfferTable["monthlyPriceUSD"] =
      row.getValue("monthlyPriceUSD");
    if (!price) return <span data-testid="priceCell">Free</span>;
    return <span data-testid="priceCell">${price}</span>;
  },
  header: ({ column }) => <ColumnHeader column={column} title="Price" />,
  sortingFn: sortByPrice,
  enableHiding: false
};

const trialColumn: StandingOfferColumn = {
  accessorKey: "trialDays",
  cell: ({ row }) => {
    const trialDays: StandingOfferTable["trialDays"] =
      row.getValue("trialDays");
    if (!trialDays) return null;
    return <span>{trialDays} days</span>;
  },
  header: ({ column }) => <ColumnHeader column={column} title="Trial days" />,
  enableHiding: false
};

const referenceColumn: StandingOfferColumn = {
  accessorKey: "reference",
  cell: ({ row }) => (
    <CellWithTooltip cellValue={row.getValue("reference") || ""} />
  ),
  header: ({ column }) => <ColumnHeader column={column} title="Reference" />,
  enableHiding: true
};

const actionsColumn: StandingOfferColumn = {
  id: "action",
  cell: ({ row }) => {
    const status: StandingOfferStatus = row.getValue("status");
    if (status === StandingOfferStatus.Cancelled) return null;

    return <StandingOfferActions id={row.original.id} />;
  },
  header: undefined
};

export const standingOfferColumns: StandingOfferColumn[] = [
  imageColumn,
  nameColumn,
  statusColumn,
  referenceColumn,
  createdColumn,
  priceColumn,
  trialColumn,
  actionsColumn
];
